import { useRef, useState, useEffect, useCallback } from 'react';
import NowPlaying from '../NowPlaying/NowPlaying';

const AudioPlayer = (props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '' });
  const [showHint, setShowHint] = useState(false);

  // Tikrai kompiuterinė naršyklė — turi pelę/touchpad, ne liečiamą ekraną
  const isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const audioRef = useRef(null);
  const canvasRef = useRef(null);

  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const isPlayingRef = useRef(false);
  const lastHeightsRef = useRef(null);
  const toastTimerRef = useRef(null);
  // Refs leidžia event listener'iams matyti naujausias reikšmes
  const volumeRef = useRef(1);
  const isMutedRef = useRef(false);

  /* ---------- FIRST-VISIT HINT ---------- */
  const dismissHint = useCallback(() => {
    setShowHint(false);
    localStorage.setItem('kb_hint_seen', '1');
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    if (!localStorage.getItem('kb_hint_seen')) {
      setShowHint(true);
      const timer = setTimeout(dismissHint, 7000);
      return () => clearTimeout(timer);
    }
  }, [isDesktop, dismissHint]);

  /* ---------- TOAST ---------- */
  /* ---------- RESUME AUDIO (po dalinimosi) ---------- */
  const resumeAudio = useCallback(() => {
    if (audioRef.current && isPlayingRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, []);

  /* ---------- TOAST ---------- */
  const showToast = useCallback((message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ visible: true, message });
    toastTimerRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 1800);
  }, []);

  /* ---------- SYNC GARSUMAS / NUTILDYMAS ---------- */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  /* ---------- KLAVIATŪROS VALDYMAS ---------- */
  useEffect(() => {
    if (!isDesktop) return;
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.code) {
        case 'Space': {
          e.preventDefault();
          dismissHint();
          setIsPlaying((prev) => !prev);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          dismissHint();
          const newVol = Math.min(1, Math.round((volumeRef.current + 0.1) * 10) / 10);
          setVolume(newVol);
          if (isMutedRef.current) {
            setIsMuted(false);
            isMutedRef.current = false;
          }
          showToast(`🔊 Garsumas: ${Math.round(newVol * 100)}%`);
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          dismissHint();
          const newVol = Math.max(0, Math.round((volumeRef.current - 0.1) * 10) / 10);
          setVolume(newVol);
          showToast(newVol === 0 ? '🔇 Garsumas: 0%' : `🔉 Garsumas: ${Math.round(newVol * 100)}%`);
          break;
        }
        case 'KeyM': {
          e.preventDefault();
          dismissHint();
          const next = !isMutedRef.current;
          setIsMuted(next);
          isMutedRef.current = next;
          showToast(next ? '🔇 Nutildyta' : '🔊 Garsas įjungtas');
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showToast, dismissHint, isDesktop]);

  /* ---------- INIT AUDIO ENGINE (once) ---------- */
  useEffect(() => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = audioCtx;
    analyserRef.current = audioCtx.createAnalyser();

    // Firefox automatiškai suspenduoja AudioContext dėl galios taupymo.
    // Jei tai nutinka grojant — atnaujinamas.
    const handleStateChange = () => {
      if (audioCtx.state === 'suspended' && isPlayingRef.current) {
        audioCtx.resume();
      }
    };
    audioCtx.addEventListener('statechange', handleStateChange);

    return () => {
      cancelAnimationFrame(animationRef.current);
      audioCtx.removeEventListener('statechange', handleStateChange);
      audioCtx.close();
    };
  }, []);

  /* ---------- PLAY / STOP ---------- */
  // Šis efektas priklauso TIK nuo isPlaying ir streamLink — ne nuo props.width.
  // Tai ištaiso klaidą, kai plotis keičiasi ir audio.play() iškviečiamas ant
  // jau grojančio elemento, sukuriant dvigubą garsą Firefox naršyklėje.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    isPlayingRef.current = isPlaying;

    if (isPlaying) {
      audioCtxRef.current.resume();

      if (!sourceRef.current) {
        sourceRef.current = audioCtxRef.current.createMediaElementSource(audio);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioCtxRef.current.destination);
      }

      audio.src = props.streamLink;
      audio.play().catch(() => {});
    } else {
      /* ----------- STOP ----------- */
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
  }, [isPlaying, props.streamLink]);

  /* ---------- VISUALIZER ---------- */
  // Atskiras efektas vizualizatoriui — gali iš naujo paleisti keičiantis
  // ekrano pločiui, nesukeliant audio.play() pakartotinio iškvietimo.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    canvas.width = window.innerWidth;
    canvas.height = props.width >= 1681 ? 300 : props.width >= 1439 ? 250 : props.width <= 394 ? 200 : 250;

    const analyser = analyserRef.current;
    analyser.fftSize = props.width >= 476 ? 128 : 32;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const barWidth = canvas.width / 2 / bufferLength;

    // Atšaukiamas ankstesnis ciklas prieš paleidžiant naują
    cancelAnimationFrame(animationRef.current);

    const drawBars = (heights) => {
      let x = 0;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // LEFT SIDE
      for (let i = 0; i < bufferLength; i++) {
        ctx.fillStyle = '#707070';
        ctx.fillRect(canvas.width / 2 - x, canvas.height - heights[i], barWidth - 5, heights[i]);
        x += barWidth;
      }
      // RIGHT SIDE
      x = 0;
      for (let i = 0; i < bufferLength; i++) {
        ctx.fillStyle = '#707070';
        ctx.fillRect(canvas.width / 2 + x, canvas.height - heights[i], barWidth - 5, heights[i]);
        x += barWidth;
      }
    };

    if (!isPlaying) {
      // Lėtas nusileidimas žemyn: naudojame paskutinio kadro aukščius
      // ir per ~600ms jas sklandžiai mažiname iki 0
      const snapshot = lastHeightsRef.current || Array.from(dataArray);
      const FADE_DURATION = 600; // ms
      const startTime = performance.now();

      const fadeOut = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / FADE_DURATION, 1);
        // easeOut kubinė kreivė — greitas pradžia, lėtas pabaiga
        const factor = 1 - progress * progress * progress;

        const heights = snapshot.map((v) => v * factor);

        drawBars(heights);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(fadeOut);
        } else {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      };

      animationRef.current = requestAnimationFrame(fadeOut);
      return () => cancelAnimationFrame(animationRef.current);
    }

    const drawVisualizer = () => {
      analyser.getByteFrequencyData(dataArray);
      const heights = Array.from(dataArray).map((v) => (props.width >= 1439 ? v / 2 : v / 3));
      lastHeightsRef.current = heights;
      drawBars(heights);
      animationRef.current = requestAnimationFrame(drawVisualizer);
    };

    drawVisualizer();

    return () => cancelAnimationFrame(animationRef.current);
  }, [isPlaying, props.width]);

  return (
    <>
      <audio ref={audioRef} crossOrigin='anonymous' preload='none' />

      <NowPlaying
        data={props.data}
        isPlaying={isPlaying}
        playStopBtn={setIsPlaying}
        resumeAudio={resumeAudio}
        width={props.width}
        canvasRef={canvasRef}
        volume={volume}
        isMuted={isMuted}
        setVolume={setVolume}
        setIsMuted={setIsMuted}
      />

      {isMuted && <div className='kb-mute-badge'>🔇 Nutildyta</div>}

      <div className={`kb-toast${toast.visible ? ' kb-toast--visible' : ''}`}>{toast.message}</div>

      {showHint && (
        <div className='kb-hint'>
          <span className='kb-hint__label'>Valdymas klaviatūra:</span>
          <span className='kb-hint__item'>
            <kbd>Space</kbd> Leisti / Stabdyti
          </span>
          <span className='kb-hint__item'>
            <kbd>↑</kbd> <kbd>↓</kbd> Garsumas
          </span>
          <span className='kb-hint__item'>
            <kbd>M</kbd> Nutildyti
          </span>
          <button className='kb-hint__close' onClick={dismissHint} aria-label='Uždaryti'>
            ✕
          </button>
        </div>
      )}
    </>
  );
};

export default AudioPlayer;
